// @theme component
import React, { useCallback, useMemo, useLayoutEffect, useRef, useState } from 'react';
import { ManagedPopup } from '@core/js/components/common/Popup';
import { useCombinedOnClose } from '@core/js/components/common/PopupManager';
import { Button } from '@core/js/components/common/Button';
import { I18n } from '@core/js/components/common/I18n';
import { useTranslations } from '@core/js/hooks/i18n';
import { ErrorMessage } from '@core/js/components/common/ErrorMessage';
import { Icon } from '@core/js/components/common/Icon';
import { setFidoAuthenticators } from './server';

import style from './index.scss';
import { IFidoAuthentificatorData } from './types';

interface INevisPasskeysRenamePopupProps {
    id: string,
    title: string,
    onSuccess: () => void,
    resendHandler: () => void,
    onClose: () => void,
    declineBtnText: string,
    acceptBtnText: string
    nameAuth: string
    fidoAuthenticators: IFidoAuthentificatorData[],
    index: number
    fetchPortlet: (options1: any, options2: any) => void,
    portletType: string
}

export const NevisPasskeysRenamePopup: React.FC<INevisPasskeysRenamePopupProps> = ({
    id,
    title,
    onSuccess,
    resendHandler,
    onClose,
    declineBtnText,
    acceptBtnText,
    nameAuth,
    fidoAuthenticators,
    index,
    fetchPortlet,
    portletType
}) => {
    const closePopup = useCombinedOnClose(onClose, id);
    const [isAbove, setLabelPosition] = useState(true);
    const [isDisabled, setDisabledState] = useState(true);
    const [isFocus, setFocus] = useState(true);
    const [isError, setErrorState] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const regExp = useMemo(() => new RegExp('^[a-zA-Z0-9# _-]{3,25}$', 'g'), []);
    const t = useTranslations();
    const placeholder = t('playtech.mobile.system.nevis-passkeys.passkeys.passkey.name.placeholder');

    const onAccept = useCallback(async () => {
        const updatedFidoAuthenticators = Object.values(fidoAuthenticators).reduce(
            (acc: IFidoAuthentificatorData[], cur: IFidoAuthentificatorData, i) => {
                i === index ? acc[i] = { ...cur, name: inputRef?.current?.value as string } : acc[i] = cur;
                return acc;
            }, []);
        try {
            await setFidoAuthenticators(updatedFidoAuthenticators);
            await fetchPortlet({ type: portletType }, { showLoader: false });
            closePopup();
        } catch (error: any) {
            const { data = {} } = error?.response || error || {};
            console.error(data);
        }
    }, [fidoAuthenticators, index, fetchPortlet, portletType, closePopup]);

    const checkRegExp = useCallback((target: string) => setErrorState(!regExp.test(target)), [regExp]);

    const onAcceptButton = useCallback((e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (!isError && inputRef?.current?.value !== nameAuth) {
            onAccept();
        }
        return null;
    }, [isError, nameAuth, onAccept]);

    const onBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        setLabelPosition(inputRef?.current?.value !== '');
        if (isDisabled) {
            e.target.placeholder = '';
        } else {
            checkRegExp(e.target.value);
        }
        setFocus(false);
    }, [checkRegExp, isDisabled]);

    const onFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        setLabelPosition(true);
        setErrorState(false);
        if (isDisabled) {
            e.target.placeholder = placeholder;
        }
        setFocus(true);
    }, [isDisabled, placeholder]);

    const onChange = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const target = e.target as HTMLInputElement;
        if (target.value && target.value !== nameAuth) {
            setDisabledState(false);
        } else {
            if (!target.value) {
                target.placeholder = placeholder;
            }
            setDisabledState(true);
        }
    }, [nameAuth, placeholder]);

    const clickOnInvalidIcon = useCallback(() => {
        inputRef?.current?.focus();
    }, []);

    useLayoutEffect(() => {
        if (inputRef?.current) {
            inputRef?.current.setAttribute('value', nameAuth);
            inputRef.current.focus();
            const positionCursor = inputRef.current.value.length;
            inputRef.current.setSelectionRange(positionCursor, positionCursor);
        }
    }, [nameAuth]);

    return (
        <ManagedPopup
            onClose={closePopup}
            title={title}
            onSuccess={onSuccess}
            className={style.popupContainer}
            resendHandler={resendHandler}
            isModal={true}>
            <div className={style.form}>
                <div className={style.inputContainerWrapper}>
                    <div className={`${style.inputContainer} ${isError && style.errorInputContainer}`}>
                        <I18n className={`${style.label} ${isAbove && style.labelAbove}`}
                              tKey={'playtech.mobile.system.nevis-passkeys.passkeys.passkey.name'}/>
                        <div className={`${style.inputWrapper} ${isFocus && style.focus}`}>
                            <input
                                ref={inputRef}
                                type="text"
                                id={'nevis-rename'}
                                className={style.containerInput}
                                onBlur={onBlur}
                                onFocus={onFocus}
                                onKeyUp={onChange}
                            />
                        </div>
                        {isError && <ErrorMessage
                            error={t('playtech.mobile.system.nevis-passkeys.passkeys.passkey.name.error')}
                            classNames={{ error: `${style.messageError} slideInDownErrors` }}
                        />}
                        {isError && <Icon
                            className={style.errorIcon}
                            onClick={clickOnInvalidIcon}
                        />}
                    </div>
                </div>
                <div className={style.popupButtons}>
                    <Button
                        type="submit"
                        view="primary"
                        color="negative"
                        onClick={closePopup}
                        className={style.button}>
                        {t(declineBtnText)}
                    </Button>
                    <Button
                        type="submit"
                        view="primary"
                        disabled={isDisabled}
                        className={style.button}
                        onClick={onAcceptButton}>
                        {t(acceptBtnText)}
                    </Button>
                </div>
            </div>
        </ManagedPopup>
    );
};
